import type { KeyboardEvent, MouseEventHandler } from 'react';
import { useRef, useState, useEffect, forwardRef } from 'react';
import styled from 'styled-components';
import type { TranslationType } from '@kiwicom/account-consts';

import Text from '../Text';
import NotificationDot from '../NotificationDot';
import useCombinedRefs from '../hooks/useCombinedRefs';

export type Props = {
  readonly active?: boolean;
  readonly setActiveId?: (idx: number) => void;
  readonly idx?: number;
  readonly children: TranslationType;
  readonly tabCount?: number;
  readonly isLast?: boolean;
  readonly tabId: string;
  readonly indicator?: boolean;
  readonly useHref?: boolean;
};

type WrapperProps = {
  dataTest: string;
  isActive: boolean;
  isLast: boolean;
};

const Wrapper = styled.a.attrs<WrapperProps>(({ dataTest, isActive }) => ({
  'data-test': dataTest,
  tabIndex: isActive ? 0 : -1, // tabbing should lead directly to tab content
}))<WrapperProps>`
  position: relative;
  background: none;
  transition: background-color 0.3s ease-out;
  text-decoration: none;
  padding: 12px;
  cursor: pointer;
  margin-right: ${({ theme, isLast }) => !isLast && theme.orbit.space400};
  outline: none;
  :hover {
    background-color: rgba(186, 199, 213, 0.15);
  }

  ::after {
    content: '';
    height: 2px;
    width: 100%;
    background-color: ${({ theme }) => theme.orbit.paletteProductNormal};
    position: absolute;
    bottom: 0px;
    left: 0px;
    opacity: ${({ isActive }) => (isActive ? '1' : '0')};
    transition: opacity 0.3s ease-out;
  }
`;

const NotificationBadgeWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 0px;
`;
const keys = {
  end: 35,
  home: 36,
  left: 37,
  right: 39,
};

const TabsHeaderItem = (
  {
    setActiveId,
    children,
    active,
    idx = -1,
    tabCount = 1,
    isLast,
    tabId,
    indicator = false,
    useHref = true,
  }: Props,
  forwardRef,
): JSX.Element => {
  const ref = useRef<HTMLAnchorElement>(null);
  const combinedRef = useCombinedRefs<HTMLAnchorElement>([forwardRef, ref]);
  const [isActive, setIsActive] = useState(active);

  const setIndex = (index: number) => {
    if (setActiveId != null && index >= 0 && index < tabCount) {
      // Prevent setting index not defined
      setActiveId(index);
    }
  };

  const onClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    setIndex(idx);
  };

  const onKeyPress = (e: KeyboardEvent<HTMLAnchorElement>) => {
    switch (e.keyCode) {
      // Prevent default if one of our key listeners are hit
      // home and end will scroll you to top or bottom of document, which we don't want in this case
      case keys.home:
        e.preventDefault();
        setIndex(0);
        break;

      case keys.end:
        e.preventDefault();
        setIndex(tabCount - 1);
        break;

      case keys.left:
        e.preventDefault();
        setIndex(idx - 1);
        break;

      case keys.right:
        e.preventDefault();
        setIndex(idx + 1);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    const button = ref.current as HTMLAnchorElement;

    // If we only rely on the active prop, it would be given focus on mount, which is not correct
    if (active === true && isActive === false && button != null) {
      // We need to switch focus when new tab is set to active using the keyboard
      button.focus();
    }

    if (active !== isActive) {
      setIsActive(active);
    }
  }, [active, isActive]);
  return (
    <Wrapper
      role="tab"
      aria-selected={active}
      onClick={onClick}
      dataTest={`tabsHeaderItem${idx}`}
      isActive={active}
      onKeyDown={onKeyPress}
      ref={combinedRef}
      isLast={isLast}
      href={useHref ? `?tab=${tabId}` : null}
    >
      {indicator && (
        <NotificationBadgeWrapper>
          <NotificationDot dataTest={`notificationBadge${idx}`} />
        </NotificationBadgeWrapper>
      )}
      <Text size="large" weight="bold" type={active ? 'primary' : 'secondary'}>
        {children}
      </Text>
    </Wrapper>
  );
};

export default forwardRef<HTMLDivElement, Props>(TabsHeaderItem);
